(() => {
  if (customElements.get('vechen-purchase-options')) return;
  const selections = new Map();
  class VechenPurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.initFrame = requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      if (this.abort || !this.isConnected) return;
      try { this.variants = JSON.parse(this.querySelector('[data-ve-variants]')?.textContent || '{}'); }
      catch { return; }
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.compact = this.dataset.compact === 'true';
      this.own = this.querySelector('[data-ve-selling-plan]');
      this.select = this.querySelector('[data-ve-frequency]');
      this.sub = this.querySelector('[data-ve-sub]');
      this.once = this.querySelector('[data-ve-once]');
      this.variantId = this.dataset.variantId;
      const saved = selections.get(this.id);
      this.isSub = saved ? saved.isSub : this.dataset.defaultSub === 'true';
      this.planId = saved?.planId || this.select?.value || '';
      this.addEventListener('change', event => {
        if (event.target === this.select) { this.planId = this.select.value; this.isSub = true; }
        else if (event.target === this.sub) this.isSub = true;
        else if (event.target === this.once) this.isSub = false;
        else return;
        this.render();
      }, opts);
      if (this.compact) ['pointerdown', 'mousedown', 'click'].forEach(type =>
        this.addEventListener(type, event => event.stopPropagation(), opts));
      this.queue = () => {
        cancelAnimationFrame(this.syncFrame);
        this.syncFrame = requestAnimationFrame(() => this.syncVariant());
      };
      ['change', 'variant:update', 'variant:change', 'product:variant-change', 'shopify:section:load'].forEach(type =>
        document.addEventListener(type, this.queue, opts));
      document.addEventListener('submit', event => {
        if (event.target === this.findForm()) { this.syncVariant(); this.writePlan(); }
      }, { ...opts, capture: true });
      this.syncVariant(true);
    }
    findForm() {
      if (this.compact) {
        const card = this.closest('product-card');
        if (!card) return null;
        const forms = [...card.querySelectorAll('form[data-type="add-to-cart-form"]')];
        return forms.find(form => this.variants[form.querySelector('[name="id"]')?.value]) || null;
      }
      return document.getElementById(this.dataset.productFormId);
    }
    bindForm(form) {
      if (form === this.form) return;
      this.observer?.disconnect();
      this.form = form;
      if (!form) return;
      if (!form.getAttribute('id')) form.setAttribute('id', this.id + '-form');
      this.own.setAttribute('form', form.getAttribute('id'));
      this.observer = new MutationObserver(this.queue);
      this.observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
    }
    syncVariant(force = false) {
      const form = this.findForm();
      this.bindForm(form);
      const id = form?.querySelector('[name="id"]')?.value || this.dataset.variantId;
      if (!this.variants[id]) { this.own.disabled = true; return; }
      if (id !== this.variantId || force) {
        this.variantId = id;
        this.render();
      } else this.writePlan();
    }
    render() {
      const value = this.variants[this.variantId];
      if (!value) return;
      const plans = value.plans || [];
      const plan = plans.find(item => item.id === this.planId) || plans[0];
      this.planId = plan?.id || '';
      if (value.requiresPlan) this.isSub = !!plan;
      if (!plan) this.isSub = false;
      this.querySelector('[data-ve-once-card]').hidden = !!value.requiresPlan;
      this.once.disabled = !!value.requiresPlan;
      this.once.checked = !this.isSub;
      this.sub.checked = this.isSub;
      this.sub.disabled = !plan;
      this.sub.value = 'plan:' + this.planId;
      this.querySelector('[data-ve-sub-card]').hidden = !plan;
      // Only the information for the selected purchase type is visible.
      this.querySelector('[data-ve-details]').hidden = !this.isSub || !plan;
      const onceDetails = this.querySelector('[data-ve-once-details]');
      if (onceDetails) onceDetails.hidden = this.isSub || !!value.requiresPlan;
      this.select.disabled = !this.isSub || !plan;
      const existingIds = [...this.select.options].map(item => item.value).join(',');
      if (existingIds !== plans.map(item => item.id).join(',')) {
        this.select.replaceChildren(...plans.map(item => new Option('Deliver every ' + item.frequency, item.id)));
      }
      this.select.value = this.planId;
      this.querySelector('[data-ve-once-price]').textContent = value.once;
      const onceSummaryPrice = this.querySelector('[data-ve-once-summary-price]');
      if (onceSummaryPrice) onceSummaryPrice.textContent = value.once;
      this.querySelector('[data-ve-sub-price]').textContent = plan?.price || '';
      const badge = this.querySelector('[data-ve-badge]');
      badge.hidden = !(plan?.saving > 0);
      badge.textContent = 'Save ' + (plan?.saving || 0) + '%';
      this.querySelector('[data-ve-saving]').textContent = plan?.copy || '';
      const next = this.querySelector('[data-ve-next]');
      next.textContent = plan?.next || '';
      next.hidden = !plan?.next;
      this.querySelectorAll('.ve-purchase__card').forEach(card =>
        card.classList.toggle('mak-sub-card--selected', !!card.querySelector('input:checked')));
      selections.set(this.id, { isSub: this.isSub, planId: this.planId });
      this.bindForm(this.findForm());
      this.writePlan();
      document.dispatchEvent(new CustomEvent('vechen:purchase-change', { detail: {
        formId: this.form?.getAttribute('id') || this.dataset.productFormId,
        sellingPlanId: this.isSub ? this.planId : '',
        selectedPlanId: this.planId,
        variantId: this.variantId
      }}));
    }
    writePlan() {
      if (!this.own) return;
      this.own.name = 'selling_plan';
      const selectedValue = this.isSub ? this.planId : '';
      if (this.own.value !== selectedValue) this.own.value = selectedValue;
      this.own.disabled = !this.isSub || !this.planId;
      if (!this.form) return;
      // There must be exactly one submitted selling_plan value per form.
      [...this.form.elements].forEach(input => {
        if (input !== this.own && input.name === 'selling_plan') input.disabled = true;
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      cancelAnimationFrame(this.syncFrame);
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.form = null;
    }
  }
  customElements.define('vechen-purchase-options', VechenPurchaseOptions);
})();
